import { FaChevronDown, FaEnvelope, FaPlusCircle, FaTag, FaUserPlus } from 'react-icons/fa';
import { FaArrowUpFromBracket, FaBell, FaCheck, FaEye, FaLink } from 'react-icons/fa6';
import { BiSolidDownload, BiXCircle } from 'react-icons/bi';
import { FiMail } from 'react-icons/fi';
import { HiTrash } from 'react-icons/hi';
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { MdEdit } from 'react-icons/md';
import { VisibilityState } from '@tanstack/react-table';
import { Listbox, Transition } from '@headlessui/react';
import _ from 'lodash';
import ConnectionsTable from '../tables/ConnectionsTable';
import SearchBar from '../forms/SearchBar';
import { Connection, ConnectionType } from '../../../redux/slices/connectionsSlice';
import WarningModal, { SimpleWarningModalProps } from '../modals/WarningModal';
import TagButton from '../buttons/TagButton';
import LocalContactModal from '../modals/LocalContactModal';
import { RootState } from '../../../redux/reducers';
import useSelectedBusiness from '../../../hooks/business/useSelectedBusiness';
import { deleteConnections } from '../../../utils/connectionUtils';
import Button from '../buttons/Button';
import OptionsButton from '../buttons/OptionsButton';
import { OnboardingStep, OptionsButtonOption } from '../../../types/misc';
import useBusinessTranslation from '../../../hooks/useBusinessTranslation';
import { isMoments } from '../../../constants';
import PersonalMomentConnectionModal from '../modals/PersonalMomentConnectionModal';
import usePersonalConnections from '../../../hooks/account/usePersonalConnections';
import { PendingBadge, UnverifiedBadge, VerifiedBadge } from '../icons/ConnectionTypeBadge';
import LocalContactInviteModal from '../modals/LocalContactInviteModal';
import adminService from '../../../services/adminService';
import InviteConnectionModal from '../modals/InviteConnectionModal';
import { BasicInfo } from '../../../types/BasicInfo';
import { SocialType } from '../../../types/Profile';
import i18n from '../../../locales/i18n';
import { CommunicationType } from '../../../types/communication';
import { hasRequiredSubscriptionForCommunication } from '../../../utils/subscriptionUtils';
import useActiveSubscription from '../../../hooks/business/useActiveSubscription';
import PremiumRequiredCard from '../cards/PremiumRequiredCard';
import ModalLayout from '../layouts/ModalLayout';
import InvitationLinkModal from '../modals/InvitationLinkModal';
import AddAdminModal from '../modals/AddAdminModal';
import inviteService from '../../../services/inviteService';

const headerColumnNameMap: Record<string, string> = {
  [BasicInfo.COMMUNICATION_NAME]: 'communicationName',
  [BasicInfo.PHONENUMBER]: 'phoneNumber',
  [BasicInfo.EMAIL]: 'email',
  [BasicInfo.ADDRESS]: 'address',
  [BasicInfo.BUSINESSNAME]: 'businessName',
  [BasicInfo.BIRTHDATE]: 'birthDate',
};

const getIds = (headers: string[], omitSelectAndName: boolean = true): string[] => {
  const columns = omitSelectAndName ? [] : ['select', 'name'];

  const basicInfoKeys = Object.keys(headerColumnNameMap);
  const basicInfo = headers.filter((header) => basicInfoKeys.includes(header));
  basicInfo.forEach((header) => {
    columns.push(headerColumnNameMap[header]);
  });

  const socials = headers.filter((header) => header in SocialType);
  socials.forEach((social) => {
    columns.push(social);
  });

  const customFields = headers.filter(
    (header) => !(header in BasicInfo) && !(header in SocialType) && header !== 'Tags',
  );
  customFields.forEach((c) => {
    columns.push(c);
  });

  columns.push('tags');

  return columns;
};

const getLabelFromColumnId = (id: string): string => {
  switch (id) {
    case 'select':
    case 'name':
    case 'communicationName':
    case 'phoneNumber':
    case 'email':
    case 'address':
    case 'businessName':
    case 'birthDate':
    case 'tags':
      return i18n.t(`page.dashboard.table.columnLabel.${id}`);
    default:
      return id;
  }
};
interface ClientListPanelProps {
  showInviteModalInitially?: boolean;
}
/**
 * A panel component that displays a list of clients and a searchbar.
 */
export default function ClientListPanel({
  showInviteModalInitially = false,
}: ClientListPanelProps): JSX.Element {
  const connections = useSelector((state: RootState) => state.connections);
  const personalConnections = usePersonalConnections();
  const tags = useSelector((state: RootState) => state.tags);
  const { onboardingStep } = useSelector((state: RootState) => state.application);
  const { t } = useBusinessTranslation();

  const [search, setSearch] = useState('');
  const [selectedConnections, setSelectedConnections] = useState<Connection[]>([]);
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const [showPopUp, setShowPopUp] = useState(false);
  const [isMobile, setMobile] = React.useState<boolean>(window.innerWidth < 1024);
  const [addLocalContactModalOpen, setAddLocalContactModalOpen] = React.useState(false);
  const [inviteConnectionModalOpen, setInviteConnectionModalOpen] = React.useState(false);
  const [inviteLinkModalOpen, setInviteLinkModalOpen] = React.useState(false);

  const [inviteMomentModalOpen, setInviteMomentModalOpen] = React.useState(false);
  const [localContactInviteModalOpen, setLocalContactInviteModalOpen] = React.useState(false);
  const [premiumModalOpen, setPremiumModalOpen] = useState(false);
  const [addAdminModalOpen, setAddAdminModalOpen] = React.useState(false);
  const activeSubscription = useActiveSubscription();

  const business = useSelectedBusiness();

  const [warningModal, setWarningModal] = React.useState<SimpleWarningModalProps>({
    open: false,
    title: '',
    description: '',
    button: '',
    cancel: '',
    cancelClassName: 'hidden',
    onCancel: () => { },
  });

  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = React.useState<'Manage' | 'Info'>(location.state?.tab || 'Manage');

  useEffect(() => {
    if (showInviteModalInitially) {
      setLocalContactInviteModalOpen(true);
    }
  }, [showInviteModalInitially]);

  useLayoutEffect(() => {
    setSelectedConnections(
      selectedConnections
        .map((x) => connections.find((y) => y.id === x.id && y.type === x.type))
        .filter((x) => x) as Connection[],
    );
  }, [connections]);

  useEffect(() => {
    setFilterTags(
      filterTags.filter((tag): boolean => tags.map((x): string => x.tagId).includes(tag)),
    );
  }, [tags]);

  const selectedConnectionsWithAddresses = selectedConnections.filter(
    (x): boolean => (x.fields as any).ADDRESS,
  );
  const selectedConnectionsWithEmails = selectedConnections.filter(
    (x): boolean => (x.fields as any).EMAIL,
  );

  useEffect((): void => {
    window.addEventListener('resize', (): void => setMobile(window.innerWidth < 1024));
  }, []);

  const filterSearch = (item: any): boolean => {
    const searchMatch = !search || 
      item.fields.FIRST_NAME?.toLowerCase().includes(search.toLowerCase()) ||
      item.fields.LAST_NAME?.toLowerCase().includes(search.toLowerCase()) ||
      `${item.fields.FIRST_NAME} ${item.fields.LAST_NAME}`.toLowerCase().includes(search.toLowerCase()) ||
      item.fields.EMAIL?.toLowerCase().includes(search.toLowerCase()) ||
      item.fields.PHONENUMBER?.toLowerCase().includes(search.toLowerCase()) ||
      item.fields.BIRTHDATE?.toLowerCase().includes(search.toLowerCase()) ||
      item.fields.BUSINESSNAME?.toLowerCase().includes(search.toLowerCase());
    
    const tagMatch = !filterTags.length ||
      filterTags.every((filterTag): boolean => item.tags?.includes(filterTag));
    
    return searchMatch && tagMatch;
  };

  const filteredConnections = useMemo(() => {
    return connections.filter(filterSearch);
  }, [connections, search, filterTags]);

  /**
   * Extracts the headers from the data, makes the spaced from camelcase.
   *
   * @param {any[]} data - The data to extract the headers from.
   * @returns {string[]} - The headers.
   */
  const extractHeaders = (data: any[]): string[] => {
    if (data.length === 0) {
      return []; // If data array is empty, return empty array
    }

    const headersSet = data.reduce(
      (acc, curr) => {
        const fields = Object.keys(curr.fields);
        fields.forEach((field) => acc.add(field));
        return acc;
      },
      new Set<string>(Object.keys(data[0].fields)),
    );
    const headers: string[] = Array.from(headersSet);
    headers.push('verificationLabel');
    headers.push('role');
    headers.push('status');
    return headers;
  };

  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    connection: Connection,
  ): void => {
    const { checked } = event.target;
    if (checked) {
      setSelectedConnections([...selectedConnections, connection]);
      setShowPopUp(true);
    } else {
      setSelectedConnections(selectedConnections.filter((c): boolean => connection !== c));
    }
  };

  const handleFilterTagClick = (id: string): void => {
    if (!filterTags.includes(id)) {
      setFilterTags([...filterTags, id]);
    } else {
      setFilterTags(filterTags.filter((tag): boolean => id !== tag));
    }
  };

  const selectAllClick = (selectAll: boolean): void => {
    if (!selectAll) {
      setSelectedConnections([]);
    } else {
      setShowPopUp(true);
      setSelectedConnections(filteredConnections);
    }
  };

  useLayoutEffect((): void => {
    if (selectedConnections.length === 0) {
      setShowPopUp(false);
    }
  }, [selectedConnections]);

  const handleDelete = async (): Promise<void> => {
    const promise = deleteConnections(selectedConnections.map(({ id, type }) => ({ id, type })));

    await toast.promise(promise, {
      pending: 'Deleting connections...',
      success: 'Connections deleted!',
      error: 'Failed to delete connections',
    });
    setSelectedConnections([]);
  };

  const navigateToPostcardPage = (): void => {
    navigate('/communication/create', {
      state: {
        type: CommunicationType.POSTCARD,
        selectedConnections: selectedConnectionsWithAddresses,
      },
    });
  };

  const navigateToEmailPage = (): void => {
    if (!hasRequiredSubscriptionForCommunication(CommunicationType.EMAIL, activeSubscription)) {
      setPremiumModalOpen(true);
      return;
    }

    navigate('/communication/create', {
      state: { type: CommunicationType.EMAIL, selectedConnections: selectedConnectionsWithEmails },
    });
  };

  const navigateToPushNotificationPage = (): void => {
    if (
      !hasRequiredSubscriptionForCommunication(CommunicationType.NOTIFICATION, activeSubscription)
    ) {
      setPremiumModalOpen(true);
      return;
    }

    navigate('/communication/create', {
      state: { type: CommunicationType.NOTIFICATION, selectedConnections },
    });
  };

  const handlePostcards = (): void => {
    if (selectedConnectionsWithAddresses.length === 0)
      setWarningModal({
        title: t('warning.selection.card'),
        description: t('warning.addressMissing.allSelected'),
        button: t('general.close'),
        cancel: t('general.cancel'),
        cancelClassName: 'hidden',
        onCancel: () => { },
        open: true,
      });
    else if (selectedConnections.length !== selectedConnectionsWithAddresses.length)
      setWarningModal({
        title: t('warning.selection.card'),
        description: t('warning.addressMissing.some'),
        button: t('general.continue'),
        cancel: t('general.cancel'),
        onCancel: navigateToPostcardPage,
        open: true,
      });
    else navigateToPostcardPage();
  };

  const handleEmail = (): void => {
    if (selectedConnectionsWithEmails.length === 0) {
      setWarningModal({
        title: t('warning.selection.email'),
        description: t('warning.emailMissing.allSelected'),
        button: t('general.close'),
        cancel: t('general.cancel'),
        open: true,
        onCancel: (): void => { },
      });
    } else if (selectedConnectionsWithEmails.length < selectedConnections.length) {
      setWarningModal({
        title: t('warning.selection.email'),
        description: t('warning.emailMissing.some'),
        button: t('general.continue'),
        cancel: t('general.cancel'),
        open: true,
        onCancel: navigateToEmailPage,
      });
    } else navigateToEmailPage();
  };

  const handleSendPushNotification = (): void => {
    navigateToPushNotificationPage();
  };

  const handleInviteMember = async (action: () => void): Promise<void> => {
    const fields = await inviteService.fetchInvite();
    if (!(!fields || fields.nonRequestedFields.length !== 10) && business?.features?.MEMBER_ADMINISTRATION) {
      setWarningModal({
        title: t('warning.inviteMember.title'),
        description: t('warning.inviteMember.description'),
        button: t('warning.inviteMember.selectData'),
        cancel: t('warning.inviteMember.continue'),
        open: true,
        onCancel: action,
        onContinue: () => navigate('/settings/requested-data'),
      });
    } else {
      action();
    }
  };

  const options = [
    {
      icon: FaLink,
      name: t('page.dashboard.clientList.addContact.link.title'),
      description: t('page.dashboard.clientList.addContact.link.subtitle'),
      onClick: () => handleInviteMember(() => setInviteLinkModalOpen(true)),
    },

    isMoments &&
      personalConnections.filter((c) => !connections.map((con) => con.userId).includes(c.userId))
        .length
      ? {
        icon: FaUserPlus,
        name: t('page.dashboard.clientList.addContact.moment.title'),
        description: t('page.dashboard.clientList.addContact.moment.subtitle'),
        onClick: () => handleInviteMember(() => setInviteMomentModalOpen(true)),
      }
      : {
        icon: FaUserPlus,
        name: t('page.dashboard.clientList.addContact.connection.title'),
        description: t('page.dashboard.clientList.addContact.connection.subtitle'),
        onClick: () => handleInviteMember(() => setInviteConnectionModalOpen(true)),
      },

      
    {
      icon: FaPlusCircle,
      name: t('page.dashboard.clientList.addContact.manual.title'),
      description: t('page.dashboard.clientList.addContact.manual.subtitle'),
      onClick: () => handleInviteMember(() => setAddLocalContactModalOpen(true)),
    },
    {
      icon: FaArrowUpFromBracket,
      name: t('page.dashboard.clientList.addContact.import.title'),
      description: t('page.dashboard.clientList.addContact.import.subtitle'),
      onClick: () => handleInviteMember(() => navigate('/settings/upload-excel')),
    },
  ].filter(Boolean) as OptionsButtonOption[];

  //For each of the tabs you can select fields to show in the table
  const headersManage = useMemo(() => {
    const allowedFields = ['FIRST_NAME', 'LAST_NAME', 'status', 'verificationLabel', 'role'];
    return extractHeaders(
      connections.length > 0
        ? connections
        : [
          {
            fields: {
              Name: 'Default',
            },
          },
        ],
    ).filter((field) => allowedFields.includes(field));
  }, [connections]);

  const allHeaders = useMemo(() => {
    return extractHeaders(
      connections.length > 0
        ? connections
        : [
          {
            fields: {
              Name: 'Default',
            },
          },
        ],
    );
  }, [connections]);
  const customFields = useMemo(
    () =>
      allHeaders.filter(
        (header) => !(header in BasicInfo) && !(header in SocialType) && header !== 'Tags' && header !== 'verificationLabel' && header !== 'role' && header !== 'status',
      ),
    [allHeaders],
  );

  const headersInfo = useMemo(() => {
    const allowedFields = ['FIRST_NAME', 'LAST_NAME', 'ADDRESS', 'PHONENUMBER', 'EMAIL', 'BIRTHDATE', 'BUSINESSNAME'];
    customFields.forEach((c) => {
      allowedFields.push(c);
    });
    return extractHeaders(
      connections.length > 0
        ? connections
        : [
          {
            fields: {
              Name: 'Default',
            },
          },
        ],
    ).filter((field) => allowedFields.includes(field));
  }, [connections]);

  //manage section
  const allColumnIdsManage = useMemo(() => getIds(headersManage), [headersManage]);
  const [columnVisibilityManage, setColumnVisibilityManage] = useState<VisibilityState>(
    _.fromPairs(allColumnIdsManage.map((x) => [x, true])),
  );

  useEffect(() => {
    setColumnVisibilityManage(_.fromPairs(allColumnIdsManage.map((x) => [x, true])));
  }, [allColumnIdsManage]);

  //info section
  const allColumnIdsInfo = useMemo(() => getIds(headersInfo), [headersInfo]);
  const [columnVisibilityInfo, setColumnVisibilityInfo] = useState<VisibilityState>(
    _.fromPairs(allColumnIdsInfo.map((x) => [x, true])),
  );

  useEffect(() => {
    setColumnVisibilityInfo(_.fromPairs(allColumnIdsInfo.map((x) => [x, true])));
  }, [allColumnIdsInfo]);

  const switchTab = (tabKey: 'Manage' | 'Info'): void => {
    if (tabKey === 'Info') {
      if (business?.features?.MEMBER_ADMINISTRATION) setActiveTab('Info');
      else setPremiumModalOpen(true);
    } else {
      setActiveTab(tabKey);
    }
  };

  const requestedData = (): void => {
    if (business?.features?.MEMBER_ADMINISTRATION) navigate('/settings/requested-data');
    else setPremiumModalOpen(true);
  };

  const hasUninvitedLocalContacts =
    filteredConnections.filter((c) => c.type === ConnectionType.LOCAL_CONTACT && !c.inviteSent).length > 0;

  return (
    <>
      <div className="flex flex-col">
        <div className="flex justify-between lg:flex-row">
          <div
            className={classNames('flex flex-col', {
              'opacity-80 blur-sm': onboardingStep !== undefined,
            })}>
            <h1 className="text-3xl font-semibold">
              {t('page.dashboard.clientList.title')}
            </h1>
            <div className="flex items-center gap-1">
              <p className={classNames('py-1 text-lg font-semibold')}>
                {`${filteredConnections.length} ${t('general.total')} `}
                {(search || filterTags.length > 0) && (
                  <span className="text-sm text-gray-500">
                    ({t('general.filtered')} {t('general.from')} {connections.length})
                  </span>
                )}
              </p>
            </div>
            <div className="mb-5 flex flex-col gap-4 md:flex-row md:items-center">
              <div className="flex items-center gap-1">
                <VerifiedBadge />
                {
                  filteredConnections.filter((c) =>
                    [
                      ConnectionType.B2C_CONNECTION,
                      ConnectionType.PERSONAL_MOMENT_CONNECTION,
                    ].includes(c.type),
                  ).length
                }{' '}
                {t('component.verifiedDisplay.verified')}
              </div>
              <div className="flex items-center gap-1">
                <PendingBadge />
                {
                  filteredConnections.filter((c) => c.type === ConnectionType.LOCAL_CONTACT && c.inviteSent)
                    .length
                }{' '}
                {t('component.verifiedDisplay.pending')}
              </div>
              <div className="flex items-center gap-1">
                <UnverifiedBadge />
                {
                  filteredConnections.filter(
                    (c) => c.type === ConnectionType.LOCAL_CONTACT && !c.inviteSent,
                  ).length
                }{' '}
                {t('component.verifiedDisplay.unverified')}
              </div>
            </div>
          </div>
          {(onboardingStep !== undefined || !business?.inviteFields) && (
            <Link to="/invite-link" state={{ view: business?.inviteFields ? 'VIEW' : 'SETUP' }}>
              <Button
                id="invite-button"
                variant="primary"
                className={classNames({
                  'border-2 border-primary-300 shadow-[0_0_16.2px_2px_#92A2EC]':
                    onboardingStep === OnboardingStep.INVITE,
                  'opacity-80 blur-sm': onboardingStep && onboardingStep !== OnboardingStep.INVITE,
                })}
                onClick={adminService.setFinishedOnboarding}>
                <FaChevronDown className="h-5 w-5" />
                {t('page.dashboard.clientList.addContact.title')}
              </Button>
            </Link>
          )}
          {business?.inviteFields && onboardingStep === undefined && (
            <OptionsButton options={options} containerClassName="left-0 sm:right-0 sm:left-auto">
              <Button variant="primary">
                <FaChevronDown className="h-5 w-5" />
                {t('page.dashboard.clientList.addContact.title')}
              </Button>
            </OptionsButton>
          )}
        </div>
        <div className="mb-6 flex gap-4 border-b border-primary-300">
          {(['Manage', 'Info'] as const).map((tabKey) => (
            <button
              key={tabKey}
              type="button"
              onClick={() => switchTab(tabKey)}
              className={`relative px-4 pb-1 pt-2 font-medium transition-all duration-150 ${activeTab === tabKey
                ? 'text-primary-700 -mb-[1px] rounded-t-md border border-primary-300 border-b-secondary-50'
                : 'text-primary-600 hover:text-primary-800'
                }`}>
              {t(`page.dashboard.clientList.tabs.${tabKey}`)}
            </button>
          ))}
        </div>
      </div>
      {hasUninvitedLocalContacts && activeTab === 'Manage' && (
        <div className="flex items-center justify-between rounded-xl bg-primary-100 p-4 text-primary-900 shadow-sm">
          <div className="flex items-center gap-3">
            <img src="/assets/figures/drop_sad.svg" alt="drop_sad" className="h-auto w-6" />
            <span className="text-sm">{t('page.dashboard.table.infoBox')}</span>
          </div>
          <Button
            variant="secondary"
            className="text-sm"
            onClick={() => setLocalContactInviteModalOpen(true)}>
            {t('page.dashboard.table.invite')}
          </Button>
        </div>
      )}
      {business?.inviteFields && onboardingStep === undefined && (
        <div className="flex w-full items-center justify-between gap-2 pb-4 pt-2 max-md:flex-col max-md:items-end">
          <SearchBar
            inputProps={{ placeholder: t('page.dashboard.table.search') }}
            search={search}
            setSearch={setSearch}
            className="md:w-80 lg:w-96"
          />
          <div className="flex flex-wrap items-center justify-end gap-2">
            {activeTab === 'Info' && (
              <Link to="/settings/export-excel">
                <Button variant="primary-outline">
                  <BiSolidDownload data-testid="share-icon" className="h-5 w-5" />
                  Download
                </Button>
              </Link>
            )}
            {isMobile || !(activeTab === 'Info') ? null : (
              <Listbox
                as="div"
                className="relative z-[2]"
                value={_.toPairs(columnVisibilityInfo)
                  .filter(([, v]) => v)
                  .map(([k]) => k)}
                multiple
                onChange={(ids) => {
                  setColumnVisibilityInfo((prev) => ({
                    ..._.fromPairs(Object.keys(prev).map((x) => [x, false])),
                    ..._.fromPairs(ids.map((x) => [x, true])),
                  }));
                }}>
                <Listbox.Button as={Button} variant="secondary">
                  <FaEye data-testid="share-icon" className="h-5 w-5" />
                  {t('page.dashboard.table.selectColumns')}
                </Listbox.Button>
                <Transition
                  as={React.Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="opacity-0 transform scale-95"
                  enterTo="opacity-100"
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0">
                  <Listbox.Options className="absolute mt-1 max-h-60 overflow-y-auto rounded-md bg-secondary-200 py-1 text-base shadow-lg">
                    {allColumnIdsInfo.map((id) => (
                      <Listbox.Option
                        className="flex cursor-pointer items-center justify-between gap-2 px-4 py-1 hover:backdrop-brightness-90"
                        key={id}
                        value={id}>
                        <div className="w-48 overflow-hidden overflow-ellipsis whitespace-nowrap text-sm">
                          {getLabelFromColumnId(id)}
                        </div>
                        {(columnVisibilityInfo[id] || columnVisibilityInfo[id] === undefined) && (
                          <FaCheck className="h-4 w-4" />
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Transition>
              </Listbox>
            )}
            {activeTab === 'Info' && (
              <Button variant="primary" onClick={() => requestedData()}>
                <MdEdit data-testid="share-icon" className="h-5 w-5" />
                {t('component.connectionsTable.configureTable')}
              </Button>
            )}
          </div>
        </div>
      )}
      {activeTab === 'Manage' && (
        <div
          className={classNames('flex h-fit w-full flex-col overflow-x-auto', {
            'opacity-80 blur-sm': onboardingStep !== undefined,
          })}>
          <ConnectionsTable
            headers={headersManage}
            data={filteredConnections}
            tags={tags}
            filteredTags={filterTags}
            onTagClick={handleFilterTagClick}
            handleCheckboxChange={handleCheckboxChange}
            selectAllClick={selectAllClick}
            selectedConnections={selectedConnections}
            columnVisibility={columnVisibilityManage}
            setColumnVisibility={setColumnVisibilityManage}
          />
        </div>
      )}
      {activeTab === 'Info' && (
        <div
          className={classNames('relative flex h-fit w-full flex-col overflow-x-auto', {
            'opacity-80 blur-sm': onboardingStep !== undefined,
          })}>
          <ConnectionsTable
            headers={headersInfo}
            data={filteredConnections}
            tags={tags}
            filteredTags={filterTags}
            onTagClick={handleFilterTagClick}
            handleCheckboxChange={handleCheckboxChange}
            selectAllClick={selectAllClick}
            selectedConnections={selectedConnections}
            columnVisibility={columnVisibilityInfo}
            setColumnVisibility={setColumnVisibilityInfo}
          />
        </div>
      )}

      {showPopUp && (
        <div className="popup-bar h-fit-content z-[2] mx-auto mb-2 flex w-fit flex-col items-center justify-center rounded-xl border border-primary-900 bg-secondary-200 py-3 shadow-xl sm:flex-row">
          <div className="flex flex-col flex-wrap items-center sm:flex-nowrap lg:flex-row">
            <div className="mb-2 flex w-full items-center justify-between border-b border-gray-400 pb-2 lg:mb-0 lg:w-fit lg:border-0 lg:pb-0">
              <span className="inline-block border-primary-900 px-4">
                {t('general.selectedWithSize', { size: selectedConnections.length })}
              </span>
              {isMobile && (
                <button
                  type="button"
                  data-testid="small-x-button"
                  className="ml-4 items-center font-semibold"
                  onClick={(): void => setShowPopUp(false)}>
                  <BiXCircle className="icon mr-2 h-5 w-5" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 border-black px-4 sm:grid-cols-3 lg:flex lg:flex-row lg:border-x">
              <button
                onClick={handleSendPushNotification}
                type="button"
                className="flex items-center text-sm font-semibold">
                <FaBell className="icon mr-2 h-5 w-5" />
                <span className="inline whitespace-nowrap">
                  {t('page.dashboard.quickAction.sendPushNotification')}
                </span>
              </button>
              <button
                onClick={handleEmail}
                type="button"
                className="flex items-center text-sm font-semibold">
                <FiMail className="icon mr-2 h-5 w-5" />
                <span className="inline whitespace-nowrap">
                  {t('page.dashboard.quickAction.sendEmail')}
                </span>
              </button>
              <button
                type="button"
                data-testid="postcard-button"
                className="flex items-center text-sm font-semibold"
                onClick={handlePostcards}>
                <FaEnvelope className="icon mr-2 h-5 w-5" />
                <span className="inline whitespace-nowrap">
                  {t('page.dashboard.quickAction.sendCard')}
                </span>
              </button>
              <TagButton connections={selectedConnections}>
                <div className="flex items-center text-sm font-semibold lg:mt-2">
                  <FaTag className="icon mr-2 h-5 w-5" />
                  <span className="inlines whitespace-nowrap">
                    {t('page.dashboard.quickAction.addTag')}
                  </span>
                </div>
              </TagButton>
              <button
                onClick={(): void =>
                  setWarningModal({
                    title: t('warning.deleteConnections.title'),
                    description: t('warning.deleteConnections.description'),
                    button: t('warning.deleteConnections.button'),
                    cancel: t('general.cancel'),
                    onCancel: handleDelete,
                    open: true,
                    saveClassName: 'bg-danger text-secondary-50',
                  })
                }
                type="button"
                data-testid="delete-connections"
                className="-mt-2 flex items-center text-sm font-semibold text-danger lg:mt-0">
                <HiTrash className="icon mr-2 h-5 w-5" />
                <span className="inline whitespace-nowrap">
                  {t('page.dashboard.quickAction.delete')}
                </span>
              </button>
            </div>
            {!isMobile && (
              <button
                type="button"
                data-testid="x-button"
                className="ml-4 flex items-center font-semibold"
                onClick={(): void => setShowPopUp(false)}>
                <BiXCircle className="icon mr-2 h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      )}
      <ModalLayout closeButton open={premiumModalOpen} setOpen={setPremiumModalOpen}>
        <PremiumRequiredCard />
      </ModalLayout>
      <WarningModal
        {...warningModal!}
        setOpen={(open: boolean): void => setWarningModal({ ...warningModal, open })}
      />
      <LocalContactInviteModal
        open={localContactInviteModalOpen}
        setOpen={setLocalContactInviteModalOpen}
      />
      {business?.inviteFields && (
        <LocalContactModal open={addLocalContactModalOpen} setOpen={setAddLocalContactModalOpen} />
      )}
      <InvitationLinkModal open={inviteLinkModalOpen} setOpen={setInviteLinkModalOpen} />
      {isMoments ? (
        <PersonalMomentConnectionModal
          open={inviteMomentModalOpen}
          setOpen={setInviteMomentModalOpen}
        />
      ) : (
        <InviteConnectionModal
          open={inviteConnectionModalOpen}
          setOpen={setInviteConnectionModalOpen}
        />
      )}
      <AddAdminModal 
        open={addAdminModalOpen} 
        setOpen={setAddAdminModalOpen} 
      />
    </>
  );
}
